import {
	Document,
	Font,
	Image,
	Page,
	Path,
	StyleSheet,
	Svg,
	Text,
	View,
} from '@react-pdf/renderer'
import clockSkipIcon from 'assets/Clockskip.png'
import skipIcon from 'assets/skip.png'
import { buildInterpolatedSeries } from 'components/shared/processes/chartNoDataSeries'
import moment, { Moment } from 'moment'
import { ReactNode } from 'react'
import { MetricCardData, MetricSectionData } from './dayMetrics'
import registerFonts from '../pdf-generate/utils/registerFont'
import { PsvPoint, PushPoint } from './nudgeMetrics'

registerFonts()
Font.registerHyphenationCallback(word => [word])

export type FeedPdfPoint = {
	date: number
	distributed_sum: number | null
	cleaned_sum: number | null
	cattle_count: number
	psv: number
}

export type RationPdfLine = {
	date: string
	titles: string[]
}

type PdfProps = {
	start: Moment
	end: Moment
	subtitle: string
	sections: MetricSectionData[]
	showCharts: boolean
	rationLines: RationPdfLine[]
	feedPoints: FeedPdfPoint[]
	pushPoints: PushPoint[]
	psvPoints: PsvPoint[]
}

const styles = StyleSheet.create({
	page: {
		paddingTop: 126,
		paddingBottom: 40,
		paddingLeft: 28,
		paddingRight: 28,
		fontFamily: 'Montserrat',
	},
	header: {
		position: 'absolute',
		top: 28,
		left: 28,
		right: 28,
	},
	logo: {
		color: '#5222D0',
		fontSize: 14,
		fontWeight: 700,
		lineHeight: 1.15,
	},
	titleRow: {
		marginTop: 16,
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
	},
	title: {
		fontSize: 16,
		fontWeight: 700,
	},
	subtitle: {
		marginTop: 2,
		fontSize: 11,
	},
	date: {
		fontSize: 11,
	},
	notice: {
		marginTop: 12,
		height: 36,
		paddingLeft: 10,
		paddingRight: 10,
		borderRadius: 8,
		border: '1px solid #E6E6E8',
		justifyContent: 'center',
	},
	noticeText: {
		fontSize: 9,
		lineHeight: 1.2,
	},
	sectionTitle: {
		marginTop: 14,
		marginBottom: 8,
		fontSize: 12,
		fontWeight: 700,
	},
	cards: {
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
	card: {
		position: 'relative',
		width: 130,
		height: 52,
		marginBottom: 6,
		paddingTop: 6,
		paddingBottom: 6,
		paddingLeft: 9,
		paddingRight: 4,
		borderRadius: 8,
		border: '1px solid #ECECEF',
		justifyContent: 'center',
	},
	valueRow: {
		flexDirection: 'row',
		flexWrap: 'nowrap',
		alignItems: 'flex-end',
	},
	valuePad: {
		paddingRight: 38,
	},
	valuePadTime: {
		paddingRight: 50,
	},
	valuePadAlert: {
		paddingRight: 64,
	},
	value: {
		fontSize: 8,
		fontWeight: 700,
	},
	unit: {
		marginLeft: 2,
		fontSize: 6,
		color: '#7F7F84',
	},
	muted: {
		marginLeft: 2,
		fontSize: 7,
		color: '#A3A3A8',
	},
	empty: {
		fontSize: 8,
		color: '#7F7F84',
	},
	label: {
		marginTop: 3,
		fontSize: 7,
		lineHeight: 1.3,
	},
	note: {
		fontSize: 7,
		color: '#7F7F84',
	},
	aside: {
		marginLeft: 2,
		fontSize: 6,
		color: '#7F7F84',
	},
	badge: {
		position: 'absolute',
		top: 2,
		right: 3,
		flexDirection: 'row',
		alignItems: 'center',
		paddingTop: 1,
		paddingBottom: 1,
		paddingLeft: 3,
		paddingRight: 3,
		borderRadius: 6,
		backgroundColor: '#F7F7F7',
	},
	badgeAlert: {
		backgroundColor: '#FDEDEC',
	},
	badgeText: {
		fontSize: 6,
		color: '#000000',
	},
	badgeTextAlert: {
		fontSize: 5,
		color: '#EB4D3D',
	},
	badgeIcon: {
		width: 6,
		height: 6,
		marginRight: 2,
	},
	chartCard: {
		marginTop: 10,
		marginBottom: 15,
		padding: 10,
		borderRadius: 12,
		border: '1px solid #E4E4E7',
	},
	chartTitle: {
		marginBottom: 6,
		fontSize: 11,
		fontWeight: 700,
	},
	caption: {
		marginBottom: 2,
		fontSize: 8,
		color: '#7F7F84',
	},
	axisCaption: {
		fontSize: 6,
		lineHeight: 1.15,
		color: '#7F7F84',
	},
	dateAxis: {
		flexDirection: 'row',
		marginLeft: 36,
	},
	dateLabel: {
		flex: 1,
		fontSize: 6,
		color: '#7F7F84',
		textAlign: 'center',
	},
	tableRow: {
		flexDirection: 'row',
		marginBottom: 4,
	},
	recordColumns: {
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
	recordColumn: {
		width: '48%',
	},
	tableDate: {
		width: 40,
		marginRight: 14,
		fontSize: 7,
		color: '#1C1C1E',
	},
	tableFeed: {
		width: 100,
		marginRight: 16,
		fontSize: 7,
		color: '#1C1C1E',
	},
	tableText: {
		fontSize: 7,
		color: '#1C1C1E',
	},
	mutedText: {
		fontSize: 8,
		color: '#7F7F84',
	},
	legendRow: {
		flexDirection: 'row',
		alignItems: 'center',
		marginTop: 4,
	},
	legendItem: {
		flexDirection: 'row',
		alignItems: 'center',
		marginRight: 10,
	},
	legendLabel: {
		marginLeft: 4,
		fontSize: 8,
		color: '#7F7F84',
	},
	pageNumber: {
		position: 'absolute',
		right: 28,
		bottom: 16,
		fontSize: 8,
		color: '#7F7F84',
	},
})

const amount = (value: number) => Math.round(value).toLocaleString('ru-RU')

const dayLabel = (value: string | number) => moment(value).format('DD.MM.YY')
const shortDay = (value: string | number) => moment(value).format('DD.MM')

const roundedTop = (x: number, top: number, bottom: number, width: number) => {
	const radius = Math.min(3, width / 2, Math.max(0, (bottom - top) / 2))
	if (radius < 0.5) {
		return `M${x} ${top} H${x + width} V${bottom} H${x} Z`
	}
	return `M${x} ${top + radius} Q${x} ${top} ${x + radius} ${top} H${x + width - radius} Q${x + width} ${top} ${x + width} ${top + radius} V${bottom} H${x} Z`
}

const PdfCard = ({ card, index }: { card: MetricCardData; index: number }) => {
	const timeChip = card.aside?.startsWith('с ') ? card.aside : undefined
	const dateAside = card.aside && !timeChip ? card.aside : undefined
	const corner = timeChip
		? { text: timeChip, tone: 'neutral' as const }
		: card.badge
	const alertChip = corner?.tone === 'alert' || corner?.tone === 'percent'

	return (
		<View
			style={index % 4 === 3 ? styles.card : [styles.card, { marginRight: 6 }]}
		>
			{corner && (
				<View
					style={alertChip ? [styles.badge, styles.badgeAlert] : styles.badge}
				>
					{corner.tone === 'percent' && (
						<Image src={skipIcon} style={styles.badgeIcon} />
					)}
					{corner.tone === 'alert' && (
						<Image src={clockSkipIcon} style={styles.badgeIcon} />
					)}
					<Text style={alertChip ? styles.badgeTextAlert : styles.badgeText}>
						{corner.text}
					</Text>
				</View>
			)}
			<View
				style={
					!corner
						? styles.valueRow
						: timeChip
							? [styles.valueRow, styles.valuePadTime]
							: alertChip
								? [styles.valueRow, styles.valuePadAlert]
								: [styles.valueRow, styles.valuePad]
				}
			>
				{card.empty ? (
					<Text style={styles.empty}>Нет данных</Text>
				) : (
					card.parts.map((part, index) => (
						<Text
							key={`${card.id}-${index}`}
							style={[
								part.tone === 'value'
									? styles.value
									: part.tone === 'muted'
										? styles.muted
										: styles.unit,
								part.tone === 'unit' && index < card.parts.length - 1
									? { marginRight: 2 }
									: {},
							]}
						>
							{part.tone === 'prefix' ? `${part.text} ` : part.text}
						</Text>
					))
				)}
				{dateAside && !card.empty && (
					<Text style={styles.aside}>{dateAside}</Text>
				)}
			</View>
			<Text style={styles.label}>{card.label}</Text>
			{card.labelNote && <Text style={styles.note}>{card.labelNote}</Text>}
		</View>
	)
}

const Legend = ({ items }: { items: { color: string; label: string }[] }) => (
	<View style={styles.legendRow}>
		{items.map(item => (
			<View key={item.label} style={styles.legendItem}>
				<Svg width={8} height={8}>
					<Path d='M4 0 a4 4 0 1 0 0.01 0 Z' fill={item.color} />
				</Svg>
				<Text style={styles.legendLabel}>{item.label}</Text>
			</View>
		))}
	</View>
)

const chartWidth = 516
const chartHeight = 148
const plotLeft = 36
const plotRight = chartWidth - 8
const plotTop = 8
const plotBottom = 112

const yOf = (value: number, max: number) =>
	plotBottom - (value / max) * (plotBottom - plotTop)

const gridLine = (y: number, key: string, solid = false) => {
	if (solid) {
		return (
			<Path
				key={key}
				d={`M${plotLeft} ${y} H${plotRight}`}
				stroke='#E6E6E8'
				strokeWidth={1}
			/>
		)
	}
	const dash = 4
	const gap = 3
	const segments = []
	for (let x = plotLeft; x < plotRight; x += dash + gap) {
		const end = Math.min(x + dash, plotRight)
		segments.push(
			<Path
				key={`${key}-${x}`}
				d={`M${x} ${y} H${end}`}
				stroke='#E6E6E8'
				strokeWidth={1}
			/>,
		)
	}
	return segments
}

const axisLabel = (value: string, y: number) => (
	<Text
		key={value + y}
		x={0}
		y={y}
		fill='#7F7F84'
		style={{ fontSize: 7, fontFamily: 'Montserrat' }}
	>
		{value}
	</Text>
)

const BarChart = ({ points }: { points: FeedPdfPoint[] }) => {
	const hasValue = points.some(
		point => (point.distributed_sum ?? 0) > 0 || (point.cleaned_sum ?? 0) > 0,
	)
	if (!hasValue) return <EmptyWave />

	const max = Math.max(
		1,
		...points.map(point =>
			Math.max(point.distributed_sum ?? 0, point.cleaned_sum ?? 0),
		),
	)
	const slot = (plotRight - plotLeft) / Math.max(points.length, 1)
	const barWidth = Math.max(4, Math.min(18, slot * 0.62))
	const ticks = [0, 0.25, 0.5, 0.75, 1]

	return (
		<Svg width={chartWidth} height={chartHeight}>
			{ticks.map(tick =>
				gridLine(yOf(max * tick, max), `bar-${tick}`, tick === 0),
			)}
			{ticks.map(tick =>
				axisLabel(amount(max * tick), yOf(max * tick, max) + 3),
			)}
			{points.flatMap((point, index) => {
				const center = plotLeft + index * slot + slot / 2
				const x = center - barWidth / 2
				const pieces = []
				if (point.distributed_sum == null && point.cleaned_sum == null) {
					pieces.push(
						<Path
							key={point.date}
							d={roundedTop(x, plotTop, plotBottom, barWidth)}
							fill='#DBDBDB'
						/>,
					)
				} else {
					const delivered = point.distributed_sum ?? 0
					const rest = point.cleaned_sum ?? 0
					const purple = delivered > rest ? delivered - rest : delivered
					const red = rest > delivered ? rest - delivered : rest
					const redTop = yOf(red, max)
					const purpleTop = yOf(red + purple, max)
					const top = purple > 0 ? purpleTop : redTop
					if (red > 0 && purple > 0) {
						pieces.push(
							<Path
								key={`${point.date}-rest`}
								d={`M${x} ${redTop} H${x + barWidth} V${plotBottom} H${x} Z`}
								fill='#EB4D3D'
							/>,
						)
					}
					if (purple > 0 || red > 0) {
						pieces.push(
							<Path
								key={`${point.date}-top`}
								d={roundedTop(
									x,
									top,
									purple > 0 && red > 0 ? redTop : plotBottom,
									barWidth,
								)}
								fill={purple > 0 ? '#5222D0' : '#EB4D3D'}
							/>,
						)
					}
				}
				pieces.push(
					<Text
						key={`${point.date}-label`}
						x={center}
						y={plotBottom + 14}
						fill='#7F7F84'
						textAnchor='middle'
						style={{ fontSize: 6, fontFamily: 'Montserrat' }}
					>
						{shortDay(point.date)}
					</Text>,
				)
				return pieces
			})}
		</Svg>
	)
}

const linePath = (points: { x: number; y: number | null }[], color: string) => {
	const segments: string[][] = []
	let current: string[] = []
	points.forEach(point => {
		if (point.y == null) {
			if (current.length) segments.push(current)
			current = []
			return
		}
		const command = current.length === 0 ? 'M' : 'L'
		current.push(`${command} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
	})
	if (current.length) segments.push(current)

	return segments.map(segment => (
		<Path
			key={segment[0]}
			d={segment.join(' ')}
			stroke={color}
			strokeWidth={1.5}
			fill='none'
		/>
	))
}

const LineChart = ({
	values,
	dates,
	color,
}: {
	values: (number | null)[]
	dates: (string | number)[]
	color: string
}) => {
	const numbers = values.filter((value): value is number => value != null)
	if (!numbers.length) return <EmptyWave />
	const max = Math.max(1, ...numbers)
	const step = (plotRight - plotLeft) / Math.max(values.length - 1, 1)
	const xAt = (index: number) => plotLeft + index * step
	const dots = values.map((value, index) => ({
		x: xAt(index),
		y: value == null ? null : yOf(value, max),
	}))
	const gap = buildInterpolatedSeries(values).map((value, index) => ({
		x: xAt(index),
		y: value == null ? null : yOf(value, max),
	}))
	const ticks = [0, 0.25, 0.5, 0.75, 1]

	return (
		<Svg width={chartWidth} height={chartHeight}>
			{ticks.map(tick =>
				gridLine(yOf(max * tick, max), `line-${tick}`, tick === 0),
			)}
			{ticks.map(tick =>
				axisLabel(amount(max * tick), yOf(max * tick, max) + 3),
			)}
			{linePath(gap, '#C4C4C8')}
			{linePath(dots, color)}
			{dates.map((date, index) => (
				<Text
					key={`${date}-${index}`}
					x={xAt(index)}
					y={plotBottom + 14}
					fill='#7F7F84'
					textAnchor='middle'
					style={{ fontSize: 6, fontFamily: 'Montserrat' }}
				>
					{shortDay(date)}
				</Text>
			))}
		</Svg>
	)
}

const EmptyWave = () => {
	const plotW = plotRight - plotLeft
	const plotH = plotBottom - plotTop
	const d = Array.from({ length: 61 }, (_, index) => {
		const progress = index / 60
		const value = 0.5 - 0.25 * Math.sin(progress * Math.PI * 3)
		const x = plotLeft + progress * plotW
		const y = plotTop + (1 - value) * plotH
		return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
	}).join(' ')

	return (
		<Svg width={chartWidth} height={chartHeight}>
			{[0, 0.25, 0.5, 0.75, 1].map(tick =>
				gridLine(
					plotBottom - tick * (plotBottom - plotTop),
					`empty-${tick}`,
					tick === 0,
				),
			)}
			{axisLabel('-', plotTop + 8)}
			{axisLabel('0', plotBottom)}
			<Path d={d} stroke='#C4C4C8' strokeWidth={1.5} fill='none' />
		</Svg>
	)
}

const RecordColumns = ({ rows }: { rows: ReactNode[] }) => {
	if (rows.length <= 7) return <View>{rows}</View>
	const middle = Math.ceil(rows.length / 2)
	return (
		<View style={styles.recordColumns} wrap={false}>
			<View style={styles.recordColumn}>{rows.slice(0, middle)}</View>
			<View style={styles.recordColumn}>{rows.slice(middle)}</View>
		</View>
	)
}

const PageHeader = ({
	subtitle,
	dateLabel,
}: {
	subtitle: string
	dateLabel: string
}) => (
	<View style={styles.header} fixed>
		<Text style={styles.logo}>SMART</Text>
		<Text style={styles.logo}>FARM</Text>
		<View style={styles.titleRow}>
			<View>
				<Text style={styles.title}>Аналитика кормления</Text>
				<Text style={styles.subtitle}>{subtitle}</Text>
			</View>
			<Text style={styles.date}>{dateLabel}</Text>
		</View>
	</View>
)

const FeedingAnalyticsPdf = ({
	start,
	end,
	subtitle,
	sections,
	showCharts,
	rationLines,
	feedPoints,
	pushPoints,
	psvPoints,
}: PdfProps) => {
	const sameDay = start.isSame(end, 'day')
	const dateLabel = sameDay
		? start.format('DD.MM.YY')
		: `${start.format('DD.MM.YY')} - ${end.format('DD.MM.YY')}`

	return (
		<Document>
			<Page size='A4' style={styles.page}>
				<PageHeader subtitle={subtitle} dateLabel={dateLabel} />
				{rationLines.map(line => (
					<View key={line.date} style={styles.notice}>
						<Text style={styles.noticeText}>
							{`${line.date} был изменен `}
							{line.titles.map((title, index) => (
								<Text key={title}>
									{index > 0 ? ', ' : ''}
									{`рацион "${title}"`}
								</Text>
							))}
							.
						</Text>
					</View>
				))}
				{sections.map(section => (
					<View key={section.id} break={showCharts && section.id === 'nudges'}>
						<Text style={styles.sectionTitle}>{section.title}</Text>
						<View style={styles.cards}>
							{section.rows.flatMap(row =>
								row.cards.map((card, index) => (
									<PdfCard key={card.id} card={card} index={index} />
								)),
							)}
						</View>
						{showCharts && section.id === 'distributions' && (
							<View>
								<View style={styles.chartCard} wrap={false}>
									<Text style={styles.chartTitle}>Расход корма и остатков</Text>
									<Text style={styles.caption}>Вес, кг</Text>
									<BarChart points={feedPoints} />
									<Legend
										items={[
											{ color: '#5222D0', label: 'Роздано корма' },
											{ color: '#EB4D3D', label: 'Остаток' },
											{ color: '#DBDBDB', label: 'Нет данных' },
										]}
									/>
								</View>
								<RecordColumns
									rows={feedPoints.map(point => (
										<View key={point.date} style={styles.tableRow}>
											<Text style={styles.tableDate}>
												{dayLabel(point.date)}
											</Text>
											<Text style={styles.tableFeed}>
												Роздано корма:{' '}
												{point.distributed_sum == null
													? 'Нет данных'
													: `${amount(point.distributed_sum)} кг`}
											</Text>
											<Text style={styles.tableText}>
												Остаток:{' '}
												{point.cleaned_sum == null
													? 'Нет данных'
													: `${amount(point.cleaned_sum)} кг`}
											</Text>
										</View>
									))}
								/>
							</View>
						)}
						{showCharts && section.id === 'nudges' && (
							<View>
								<View style={styles.chartCard} wrap={false}>
									<Text style={styles.chartTitle}>Подталкивания</Text>
									<Text style={styles.axisCaption}>Количество</Text>
									<Text style={styles.axisCaption}>подталкиваний</Text>
									<LineChart
										values={pushPoints.map(point => point.count)}
										dates={pushPoints.map(point => point.date)}
										color='#F09A37'
									/>
									<Legend
										items={[
											{ color: '#F09A37', label: 'Подталкивания' },
											{ color: '#DBDBDB', label: 'Нет данных' },
										]}
									/>
								</View>
								<RecordColumns
									rows={pushPoints.map(point => (
										<View key={point.date} style={styles.tableRow}>
											<Text style={styles.tableDate}>
												{dayLabel(point.date)}
											</Text>
											<Text style={styles.tableText}>
												Подталкиваний:{' '}
												{point.count == null
													? 'Нет данных'
													: `${amount(point.count)} раз/день`}
											</Text>
										</View>
									))}
								/>
							</View>
						)}
						{showCharts && section.id === 'psv' && (
							<View>
								<View style={styles.chartCard} wrap={false}>
									<Text style={styles.chartTitle}>
										Потребление сухого вещества
									</Text>
									<Text style={styles.axisCaption}>ПСВ,</Text>
									<Text style={styles.axisCaption}>кг/гол</Text>
									<LineChart
										values={psvPoints.map(point => point.psv)}
										dates={psvPoints.map(point => point.date)}
										color='#65C466'
									/>
									<Legend
										items={[
											{ color: '#65C466', label: 'ПСВ' },
											{ color: '#DBDBDB', label: 'Нет данных' },
										]}
									/>
								</View>
								<RecordColumns
									rows={psvPoints.map(point => (
										<View key={point.date} style={styles.tableRow}>
											<Text style={styles.tableDate}>
												{dayLabel(point.date)}
											</Text>
											<Text style={styles.tableText}>
												Среднее ПСВ:{' '}
												{point.psv == null
													? 'Нет данных'
													: `${amount(point.psv)} кг/гол`}
											</Text>
										</View>
									))}
								/>
							</View>
						)}
					</View>
				))}
				<Text
					style={styles.pageNumber}
					render={({ pageNumber, totalPages }) => `${pageNumber}/${totalPages}`}
					fixed
				/>
			</Page>
		</Document>
	)
}

export const feedingAnalyticsFileName = ({
	start,
	end,
	status,
	groups,
}: {
	start: Moment
	end: Moment
	status: string
	groups: string
}) => {
	const date = start.isSame(end, 'day')
		? start.format('DD.MM.YY')
		: `${start.format('DD.MM.YY')}-${end.format('DD.MM.YY')}`
	return ['Аналитика кормления', status, groups, date]
		.filter(Boolean)
		.join(' ')
		.replace(/[\\/:*?"<>|]/g, '')
}

export default FeedingAnalyticsPdf
